// Ported from hotwire-flow (MIT, @hotwirebits/flow) into conek-ui. The engine
// logic is upstream's; identifiers/events/classes follow the conek contract in
// docs/superpowers/specs/2026-09-09-flow-graph-components-design.md §2.
import { Controller } from "@hotwired/stimulus"

// Node toolbar — floating toolbar above selected nodes.
export default class extends Controller {
  static values = {
    nodeId: { type: String, default: "" },
    isVisible: { type: Boolean, default: false },
    position: { type: String, default: "top" },
    offset: { type: Number, default: 10 }
  }

  connect() {
    // M6: visual chrome (background/border/shadow) lives on the
    // .ui-flow-node-toolbar class in flow.css as theme tokens — the controller
    // sets only functional inline styles and never clobbers host inline styles
    // with a cssText blob.
    this.element.classList.add("ui-flow-node-toolbar")
    this.element.style.position = "absolute"
    this.element.style.display = this.isVisibleValue ? "flex" : "none"
    this.element.style.pointerEvents = "auto"
    this._onSelect = () => this._updateVisibility()
    document.addEventListener("ui--flow:nodeselect", this._onSelect)
    document.addEventListener("ui--flow:nodedeselect", this._onSelect)
    this._updatePosition()
  }

  disconnect() {
    document.removeEventListener("ui--flow:nodeselect", this._onSelect)
    document.removeEventListener("ui--flow:nodedeselect", this._onSelect)
  }

  _updateVisibility() {
    const nodeEl = this.element.closest("[data-ui--flow-node]")
    if (!nodeEl) return
    const isVisible = nodeEl.classList.contains("selected") || this.isVisibleValue
    this.element.style.display = isVisible ? "flex" : "none"
    if (isVisible) this._updatePosition()
  }

  _updatePosition() {
    const nodeEl = this.element.closest("[data-ui--flow-node]")
    if (!nodeEl) return
    const pos = this.positionValue
    // Counter-scale (UX audit): the toolbar lives INSIDE the transformed
    // pane, so every px of it scaled with zoom. The engine exports the live
    // zoom as --ui-flow-zoom-inv on the canvas root (_applyTransform) —
    // composing scale(var()) into the existing translate keeps the centered
    // placement while the bar renders at a constant screen size, live as
    // zoom changes (no viewportchange listener needed). transform-origin is
    // the edge glued to the node so the counter-scale grows away from it.
    // The marginBottom/marginTop offset still scales with zoom — a few px of
    // drift, accepted.
    if (pos === "top") {
      this.element.style.bottom = "100%"
      this.element.style.left = "50%"
      this.element.style.transform = "translateX(-50%) scale(var(--ui-flow-zoom-inv, 1))"
      this.element.style.transformOrigin = "bottom center"
      this.element.style.marginBottom = `${this.offsetValue}px`
    } else if (pos === "bottom") {
      this.element.style.top = "100%"
      this.element.style.left = "50%"
      this.element.style.transform = "translateX(-50%) scale(var(--ui-flow-zoom-inv, 1))"
      this.element.style.transformOrigin = "top center"
      this.element.style.marginTop = `${this.offsetValue}px`
    }
  }

  show() { this.element.style.display = "flex"; this._updatePosition() }
  hide() { this.element.style.display = "none" }
}
